import { useToast } from '@chakra-ui/react';
import { Modal, ModalContent, ModalHeader, ModalCloseButton } from '../ui/modal';
import { useEffect, useState, useCallback, useRef } from 'react';
import Draggable from 'react-draggable';
import Spinner from 'web2/app/components/spinner';
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
// } from '../ui/dropdown-menu';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { useDisclosure } from 'web2/app/misc/wrappers/use-disclosure';
import { cn } from 'web2/app/utils/cn';
import { useRouter } from '@tanstack/react-router';
import { ThemeWheel } from 'shared/misc/components/ThemeSwitcher';
import { isUserPremium as isUserPremiumGate } from 'shared/gates';
import { Controller, useForm } from 'react-hook-form';
import { useAuth } from 'shared/misc/hooks/useAuth';
import { Button } from '../button';
import { X } from 'lucide-react';
import { PerfImageFromFileFragment, ProfileThemeType } from 'shared/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from 'shared/__generated__';
import { getPublicUrlFromKey } from 'shared/s3';
import PerfImageFromFile from 'web2/app/components/by-type/images/perf-image-from-file';
import {
  THEME_CONFIG,
  ThemeColors,
  ThemeColorMap,
  generateThemeFromMainColors,
  CustomThemeInputColorKeys,
} from 'shared/misc/theme/custom/custom-theme-colors';
import { debounce } from 'lodash';
import useFileUpload from 'shared/misc/hooks/useFileUpload';
import { RiDeleteBin2Fill } from 'react-icons/ri';
import { genCDN, extractCDNKey } from 'shared/cdn';
import { ChromePicker, ColorResult } from 'react-color';
import { Popover, Portal, PopoverContent, PopoverTrigger } from '../ui/popover';
import { BiSolidEyedropper } from 'react-icons/bi';

const GET_USER_CUSTOM_THEMES = gql(/* GraphQL */ `
  query GetUserCustomThemes {
    me {
      id
      customThemes {
        id
        name
        colors
      }
    }
  }
`);

const CREATE_CUSTOM_THEME = gql(/* GraphQL */ `
  mutation CreateCustomTheme($name: String!, $colors: JSON!) {
    createCustomTheme(name: $name, colors: $colors) {
      id
      name
      colors
    }
  }
`);

const UPDATE_PROFILE_THEME = gql(/* GraphQL */ `
  mutation UpdateProfileThemeModal($theme: ProfileThemeType!, $customThemeId: String) {
    updateProfileTheme(profileTheme: $theme, customThemeId: $customThemeId) {
      id
      profileTheme
      customThemeId
    }
  }
`);

type Props = {
  modalState?: ReturnType<typeof useDisclosure>;
  setTheme: (theme: ProfileThemeType, customThemeColorValues?: ThemeColors) => void;
};

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  const handleColorChange = useCallback((newColor: ColorResult) => {
    onChange(newColor.hex);
  }, [onChange]);

  return (
    <div className="flex items-center w-full gap-2 relative">
      <div
        className="w-8 h-8 cursor-pointer border border-gray-300"
        style={{ backgroundColor: color }}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && (
        <div ref={pickerRef} className="absolute left-0 top-[100%] z-[99999999999]">
          <ChromePicker
            disableAlpha
            color={color}
            onChange={handleColorChange}
            className="w-full"
          />
        </div>
      )}
      <input
        type="text"
        className="ml-2 w-full text-sm text-brand-main bg-transparent bg-image-none focus:outline-none focus:ring-0 focus:ring-offset-0 relative"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsOpen(true)}
      />
      <button
        type="button"
        className="absolute hidden md:block right-0 top-0 mt-2 mr-2"
        onClick={async () => {
          if (typeof window !== 'undefined' && 'EyeDropper' in window) {
            try {
              // @ts-ignore - EyeDropper API is not yet in TypeScript types
              const eyeDropper = new window.EyeDropper();
              const result = await eyeDropper.open();
              onChange(result.sRGBHex);
            } catch (error) {
              console.error('Eyedropper failed:', error);
            }
          } else {
            console.warn('EyeDropper is not supported in this browser');
          }
        }}
      >
        <BiSolidEyedropper size={16} className="text-brand-main" />
      </button>
    </div>
  );
};

export default function EditThemeModal({ modalState: _modalState, setTheme }: Props) {
  const toast = useToast();
  const { user } = useAuth();
  const isUserPremium = user && isUserPremiumGate(user);
  const modalState = _modalState || useDisclosure();
  const [isCustomizing, setIsCustomizing] = useState(
    user?.profileTheme === ProfileThemeType.Custom || false,
  );

  const router = useRouter();
  const [createCustomTheme] = useMutation(CREATE_CUSTOM_THEME);
  const [updateProfileTheme] = useMutation(UPDATE_PROFILE_THEME, {
    update(cache, { data }) {
      if (data?.updateProfileTheme) {
        cache.modify({
          id: cache.identify(data.updateProfileTheme),
          fields: {
            profileTheme: () => data.updateProfileTheme.profileTheme,
            customThemeId: () => data.updateProfileTheme.customThemeId,
          },
        });
      }
    },
  });

  // const { data: customThemesData } = useQuery(GET_USER_CUSTOM_THEMES);

  const defaultCustomTheme = generateThemeFromMainColors(
    user?.customTheme?.colors ??
      ({
        background: '#ffffff',
        main: '#000000',
        highlight: '#FFBB00',
        backgroundSize: user?.customTheme?.colors?.backgroundSize || 'auto',
        backgroundRepeat: user?.customTheme?.colors?.backgroundRepeat || 'repeat',
        backgroundPosition: user?.customTheme?.colors?.backgroundPosition || 'center',
      } as ThemeColors),
  );

  type FormValues = {
    profileTheme: string;
    backgroundImage: Parameters<typeof PerfImageFromFile>[0]['file'];
    font: string;
    customColors: ThemeColors;
    customThemeName: string;
  };

  const { control, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      profileTheme: user?.profileTheme || 'DEFAULT',
      backgroundImage: undefined,
      font: 'DEFAULT',
      customColors: defaultCustomTheme as ThemeColors,
      customThemeName: user?.customTheme?.name || '',
    },
  });

  useEffect(() => {
    if (user?.customTheme?.colors?.backgroundImage) {
      const imageUrl = user.customTheme.colors.backgroundImage;
      const key = extractCDNKey(imageUrl);
      if (key) {
        setValue('backgroundImage', {
          key,
          url: imageUrl,
          name: 'background-image',
          extension: 'webp',
          createdAt: new Date().toISOString(),
          isUploaded: true,
          isCompressed: true,
          isFailed: false,
          __typename: 'File',
        } as PerfImageFromFileFragment);
      }
    }
  }, [user?.customTheme?.colors?.backgroundImage]);

  const {
    openPicker: open,
    nodes,
    isUploadingAndSigning: isUploading,
  } = useFileUpload({
    onUpload: (file) => {
      setValue('backgroundImage', file);
      if (!values.customColors.backgroundSize) {
        setValue('customColors.backgroundSize', 'auto');
      }
      if (!values.customColors.backgroundRepeat) {
        setValue('customColors.backgroundRepeat', 'repeat');
      }
      if (!values.customColors.backgroundPosition) {
        setValue('customColors.backgroundPosition', 'center');
      }
    },
  });

  const values = watch();

  const updateTheme = useCallback(
    debounce((theme: ProfileThemeType, customThemeColorValues?: ThemeColors) => {
      setTheme(theme, customThemeColorValues);
    }, 300),
    [setTheme],
  );

  useEffect(() => {
    if (isCustomizing) {
      const generatedTheme = generateThemeFromMainColors(values.customColors);
      if (values.backgroundImage?.key) {
        generatedTheme.backgroundImage = genCDN(values.backgroundImage.key, 100, 'webp');
      } else {
        generatedTheme.backgroundImage = undefined;
      }
      generatedTheme.backgroundSize = values.customColors.backgroundSize;
      generatedTheme.backgroundRepeat = values.customColors.backgroundRepeat;
      generatedTheme.backgroundPosition = values.customColors.backgroundPosition;
      updateTheme(ProfileThemeType.Custom, generatedTheme);
    } else {
      updateTheme(values.profileTheme as ProfileThemeType);
    }
  }, [
    values.profileTheme,
    isCustomizing,
    values.customColors.background,
    values.customColors.highlight,
    values.customColors.titleBackground,
    values.customColors.main,
    values.backgroundImage?.key,
    values.customColors.backgroundSize,
    values.customColors.backgroundRepeat,
    values.customColors.backgroundPosition,
    updateTheme,
  ]);

  const handleSaveCustomTheme = async () => {
    try {
      const { data } = await createCustomTheme({
        variables: {
          name: values.customThemeName,
          colors: {
            ...generateThemeFromMainColors(values.customColors),
            ...(values.backgroundImage
              ? {
                  backgroundImage: genCDN(values.backgroundImage?.key, 100, 'webp'),
                  backgroundSize: values.customColors.backgroundSize || 'auto',
                  backgroundRepeat: values.customColors.backgroundRepeat || 'repeat',
                  backgroundPosition: values.customColors.backgroundPosition || 'center',
                }
              : {}),
          },
        },
      });

      if (data?.createCustomTheme) {
        await updateProfileTheme({
          variables: {
            theme: ProfileThemeType.Custom,
            customThemeId: data.createCustomTheme.id,
          },
        });

        setIsCustomizing(false);
        modalState.onClose();
      }
    } catch (error) {
      console.error('Failed to save custom theme:', error);
    }
  };

  const handleSaveTheme = async () => {
    try {
      await updateProfileTheme({
        variables: {
          theme: values.profileTheme,
          customThemeId: null,
        },
      });
      modalState.onClose();
    } catch (error) {
      console.error('Failed to update theme:', error);
    }
  };

  const isThemePremium =
    values.profileTheme === ProfileThemeType.Custom || THEME_CONFIG[values.profileTheme as ProfileThemeType]?.isPremium;

  return (
    <Modal open={modalState.isOpen} onOpenChange={modalState.onToggle}>
      {nodes}
      <Draggable handle=".modal-drag-handle" cancel=".no-drag .chrome-picker *" bounds="body">
        <ModalContent
          onPointerDownOutside={(e) => e.preventDefault()}
          className={cn(
            'modal-drag-handle bg-brand-background shadow-[0_0_10px_0_rgba(0,0,0,0.1)]',
            'fixed inset-0 w-full h-full overflow-y-auto',
            'm-0 l-0 b-0 md:left-[unset] md:top-[unset] md:!bottom-[20px] md:!right-[20px]',
            'md:max-w-[60vw] md:max-h-[90vh] md:h-auto',
            `md:!w-[400px] md:!h-[${
              isCustomizing ? '850px' : '475px'
            }] pb-0 flex flex-col bg-brand-background`,
            'cursor-move overflow-y-auto text-brand-main',
          )}
          dontRenderOverlay
          hideCloseButton
        >
          <ModalHeader className="p-0 m-0 h-[50px]">
            <>
              <div className="typography-heading-md flex justify-between items-center">
                <span>{isCustomizing ? 'Custom Theme' : 'Edit Theme'}</span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={!isUserPremium && isThemePremium ? () => router.navigate({ to: '/settings/premium' }) : (isCustomizing ? handleSaveCustomTheme : handleSaveTheme)}
                >
                  {!isUserPremium && isThemePremium ? 'Upgrade' : (isCustomizing ? 'Save Theme' : 'Update Theme')}
                </Button>
              </div>
              {!isCustomizing && (
                <Button
                  className="no-drag cursor-pointer typography-heading-sm text-brand-main opacity-50 text-[12px]"
                  variant="textOnly"
                  size="sm"
                  onClick={() => {
                    if (isUserPremium) {
                      setIsCustomizing(true);
                      setValue('profileTheme', ProfileThemeType.Custom);
                    } else {
                      router.navigate({ to: '/settings/premium' });
                    }
                  }}
                >
                  Create Custom Theme{' '}
                  <span className="text-brand-highlight">
                    {isUserPremium ? '(NEW!)' : '(Upgrade)'}
                  </span>
                </Button>
              )}
            </>
          </ModalHeader>
          <ModalCloseButton className="absolute top-[10px] right-[10px]">
            <X size={14} />
          </ModalCloseButton>
          <div className="flex-1 h-full w-full">
            <form className="h-full w-full flex flex-col justify-between">
              {/* <div>
                <h2 className="typography-heading-sm text-[16px] opacity-50 pb-[8px]">
                  Change Font
                </h2>
                <div className="flex items-center gap-[8px]">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className={cn(
                          'w-full relative after:border-transparent after:content-[""] after:border-x-4 after:border-t-4',
                          'after:border-b-0 after:border-solid after:border-t-brand-main after:absolute',
                          'after:right-2 after:top-1/2 after:-translate-y-1/2 after:pointer-events-none',
                          'after:transition-all after:duration-300 after:ease-in-out text-left',
                        )}
                        variant="secondary"
                        size="sm"
                      >
                        Select Font
                      </Button>
                    </DropdownMenuTrigger>
                  </DropdownMenu>
                </div>
              </div> */}
              {isCustomizing && (
                <>
                  <div>
                    <h2 className="typography-heading-sm text-[16px] opacity-50 py-[8px]">
                      Add Background Image
                    </h2>
                    <div className="w-full flex items-start gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          open();
                        }}
                        size="sm"
                      >
                        Upload
                      </Button>
                      {isUploading && (
                        <div className="flex relative items-center gap-2 w-[50px] h-[50px]">
                          <span className="w-full h-full bg-brand-main opacity-20" />
                          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <Spinner size="sm" />
                          </span>
                        </div>
                      )}
                      {values.backgroundImage && (
                        <>
                          <div className="relative">
                            <PerfImageFromFile
                              file={values.backgroundImage}
                              width={100}
                              height={100}
                              className="mb-2"
                              useFallbackAvatarSrc
                            />
                            <button
                              type="button"
                              onClick={() => setValue('backgroundImage', undefined)}
                              className="z-11 absolute top-1 right-1 p-1 bg-red-500 text-white"
                              aria-label="Remove image"
                            >
                              {/* @ts-ignore */}
                              <RiDeleteBin2Fill size={16} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    {values.backgroundImage && (
                      <div className="flex flex-row items-start gap-[8px] mb-[12px] w-full">
                        <div className="flex items-center gap-[8px] pl-[8px] text-brand-main">
                          <Controller
                            control={control}
                            name="customColors.backgroundSize"
                            render={({ field }) => (
                              <RadioGroup
                                className="flex flex-col items-start gap-[2px] no-drag text-brand-main z-10"
                                {...field}
                                value={field.value ?? 'auto'}
                                onValueChange={field.onChange}
                              >
                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="auto"
                                    value="auto"
                                  />
                                  <label className="cursor-pointer" htmlFor="auto">
                                    Auto
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="contain"
                                    value="contain"
                                  />
                                  <label className="cursor-pointer" htmlFor="contain">
                                    Contain
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="cover"
                                    value="cover"
                                  />
                                  <label className="cursor-pointer" htmlFor="cover">
                                    Cover
                                  </label>
                                </div>
                              </RadioGroup>
                            )}
                          />
                        </div>
                        <div className="flex items-center gap-[8px] pl-[8px] text-brand-main">
                          <Controller
                            control={control}
                            name="customColors.backgroundRepeat"
                            render={({ field }) => (
                              <RadioGroup
                                className="flex flex-col items-start gap-[2px] no-drag text-brand-main z-10"
                                {...field}
                                value={field.value ?? 'repeat'}
                                onValueChange={field.onChange}
                              >
                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="repeat"
                                    value="repeat"
                                  />
                                  <label className="cursor-pointer" htmlFor="repeat">
                                    Repeat
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="no-repeat"
                                    value="no-repeat"
                                  />
                                  <label className="cursor-pointer" htmlFor="no-repeat">
                                    No Repeat
                                  </label>
                                </div>
                              </RadioGroup>
                            )}
                          />
                        </div>
                        <div className="flex items-center gap-[8px] pl-[8px] text-brand-main">
                          <Controller
                            control={control}
                            name="customColors.backgroundPosition"
                            render={({ field }) => (
                              <RadioGroup
                                className="flex flex-col items-start gap-[2px] no-drag text-brand-main z-10"
                                {...field}
                                value={field.value ?? 'center'}
                                onValueChange={field.onChange}
                              >
                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="center"
                                    value="center"
                                  />
                                  <label className="cursor-pointer" htmlFor="center">
                                    Center
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="top"
                                    value="top"
                                  />
                                  <label className="cursor-pointer" htmlFor="top">
                                    Top
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="bottom"
                                    value="bottom"
                                  />
                                  <label className="cursor-pointer" htmlFor="bottom">
                                    Bottom
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="left"
                                    value="left"
                                  />
                                  <label className="cursor-pointer" htmlFor="left">
                                    Left
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <RadioGroupItem
                                    className="text-brand-main border-brand-main mr-[4px]"
                                    id="right"
                                    value="right"
                                  />
                                  <label className="cursor-pointer" htmlFor="right">
                                    Right
                                  </label>
                                </div>
                              </RadioGroup>
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-[8px] no-drag cursor-default">
                    <h2 className="typography-heading-sm text-[16px] opacity-50 pb-[8px]">
                      Change Colors
                    </h2>
                    <div className="flex flex-col gap-[8px] relative">
                      {Object.values(CustomThemeInputColorKeys).map((key) => (
                        <div
                          key={key}
                          className="border-solid border-brand-main border-[1px] flex flex-row justify-start align-start no-drag relative p-2"
                        >
                          <Controller
                            control={control}
                            name={`customColors.${key}`}
                            render={({ field }) => (
                              <ColorPicker color={field.value} onChange={field.onChange} />
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div className="relative h-[150px] overflow-y-hidden">
                <Controller
                  control={control}
                  name="profileTheme"
                  render={({ field: { ref, onChange, value } }) => (
                    <div
                      className={cn(
                        'absolute w-[350px] h-[350px] top-[20px] left-1/2 transform',
                        '-translate-x-1/2 rounded-[50%] p-[24px] bg-black/10',
                      )}
                    >
                      <ThemeWheel
                        withName
                        withGates
                        onChange={(theme) => {
                          onChange(theme);
                          if (theme !== ProfileThemeType.Custom) {
                            setIsCustomizing(false);
                          }
                        }}
                        value={isCustomizing ? ProfileThemeType.Custom : value}
                        size={302}
                        enableCustomTheme={true}
                        onCustomThemeClick={() => setIsCustomizing(true)}
                      />
                    </div>
                  )}
                />
              </div>
            </form>
          </div>
        </ModalContent>
      </Draggable>
    </Modal>
  );
}
